import { Button, Input } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/design';
import type { IMailVersion } from '../../api/mail-template-contracts';
import { formatEt } from './template-fields';

/**
 * Publishing a variant's text: now, or from a date and time in the business zone (ET), with a note
 * of what changed. A future date schedules the version; one already scheduled is replaced.
 */

const ZONE = 'America/New_York';

/** The business zone's wall clock minus UTC at an instant, in milliseconds. */
function zoneOffset(at: number): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: ZONE,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  }).formatToParts(new Date(at));
  const get = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? 0);
  const wall = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'));
  return wall - Math.floor(at / 60_000) * 60_000;
}

/** "2026-11-15" and "09:00" on the ET wall clock as an instant. */
export function etInstant(date: string, time: string): Nullable<Date> {
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const clock = /^(\d{2}):(\d{2})$/.exec(time);
  if (!day || !clock) return null;
  const wall = Date.UTC(Number(day[1]), Number(day[2]) - 1, Number(day[3]), Number(clock[1]), Number(clock[2]));
  let at = wall;
  for (let pass = 0; pass < 2; pass++) at = wall - zoneOffset(at);
  return new Date(at);
}

/** Tomorrow in ET, as a date input reads it. */
function tomorrowEt(): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: ZONE }).format(new Date(Date.now() + 86_400_000));
  return parts.slice(0, 10);
}

export interface IPublishChoice {
  effectiveAt: Nullable<string>;
  note: string;
}

export function PublishDialog({
  open,
  onOpenChange,
  title,
  scheduled,
  initialNote,
  busy,
  refusal,
  onPublish
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** "Submission confirmation for FA". */
  title: string;
  scheduled: Nullable<Pick<IMailVersion, 'version' | 'effectiveAt'>>;
  initialNote: string;
  busy: boolean;
  refusal: Nullable<string>;
  onPublish: (choice: IPublishChoice) => void;
}) {
  const [when, setWhen] = React.useState<'now' | 'later'>('now');
  const [date, setDate] = React.useState(tomorrowEt);
  const [time, setTime] = React.useState('09:00');
  const [note, setNote] = React.useState(initialNote);
  const id = React.useId();

  React.useEffect(() => {
    if (!open) return;
    setWhen('now');
    setDate(tomorrowEt());
    setTime('09:00');
    setNote(initialNote);
  }, [open, initialNote]);

  const at = when === 'later' ? etInstant(date, time) : null;
  const whenProblem =
    when === 'later' && (!at ? 'A date and a time.' : at.getTime() <= Date.now() ? 'A moment still to come.' : null);
  const noteProblem = note.includes('{{') ? 'No placeholders in a note.' : null;
  const blocked = Boolean(whenProblem || noteProblem) || busy;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (blocked) return;
    onPublish({ effectiveAt: at ? at.toISOString() : null, note: note.trim() });
  };

  const radio = (value: 'now' | 'later', label: string) => (
    <label className="flex items-center gap-2">
      <input
        type="radio"
        name={`${id}-when`}
        value={value}
        checked={when === value}
        onChange={() => setWhen(value)}
        className="size-4 accent-[var(--color-teal)]"
      />
      {label}
    </label>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-publish-dialog="">
        <DialogHeader>
          <DialogTitle>Publish</DialogTitle>
          <DialogDescription>{title}</DialogDescription>
        </DialogHeader>
        <DialogBody as="form" id={`${id}-form`} onSubmit={submit} className="text-[0.8125rem]">
          <fieldset className="grid gap-2">
            <legend className="mb-1 font-medium">Takes effect</legend>
            {radio('now', 'Now — the next mail sent uses it')}
            {radio('later', 'On a date')}
            {when === 'later' && (
              <div className="ml-6 flex flex-wrap items-center gap-2">
                <Input
                  type="date"
                  aria-label="Date"
                  value={date}
                  onChange={(event) => setDate(event.target.value)}
                  className="h-8 w-40"
                />
                <Input
                  type="time"
                  aria-label="Time"
                  value={time}
                  onChange={(event) => setTime(event.target.value)}
                  className="h-8 w-28"
                />
                <span className="text-muted-foreground">ET</span>
              </div>
            )}
            {whenProblem && <p className="ml-6 text-xs text-destructive">{whenProblem}</p>}
            {scheduled && (
              <p className="text-xs text-muted-foreground">
                Version {scheduled.version}, scheduled for {formatEt(scheduled.effectiveAt ?? '')}, is withdrawn.
              </p>
            )}
          </fieldset>
          <div className="grid gap-1">
            <label htmlFor={`${id}-note`} className="font-medium">
              What changed? <span className="font-normal text-muted-foreground">(optional)</span>
            </label>
            <Input
              id={`${id}-note`}
              value={note}
              maxLength={500}
              aria-invalid={noteProblem ? true : undefined}
              onChange={(event) => setNote(event.target.value)}
              className="h-8"
            />
            {noteProblem && <p className="text-xs text-destructive">{noteProblem}</p>}
          </div>
          {refusal && (
            <p role="alert" className="text-destructive">
              {refusal}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" form={`${id}-form`} size="sm" disabled={blocked}>
            {busy ? 'Publishing…' : when === 'later' ? 'Schedule' : 'Publish now'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
